import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { marketAPI, signalsAPI } from '../../services/api'
import type { SparklineSeries, TradingSignal } from '../../types'
import { formatPrice } from '../../utils/marketPrice'
import { formatPnlPct } from '../../utils/pnl'
import { signalPnlPct } from '../../utils/signalPnl'
import CoinIcon from '../CoinIcon'
import { formatRemaining, parseApiDate, signalExpiry } from '../../utils/signalExpiry'
import GradientSparkline from './GradientSparkline'
import TerminalPanel from './TerminalPanel'
import { directionOf, useChartTheme } from './chartTheme'
import { displaySymbol } from '../../utils/displaySymbol'

export const MAX_POSITION_ROWS = 6

// Matches the sparkline endpoint's server-side cache TTL.
export const POSITIONS_POLL_MS = 60_000

// Newest first, so every open signal sits well inside this: the platform caps
// open positions far below it.
const LIST_LIMIT = 100

const isOpenSignal = (signal: TradingSignal) => signal.status === 'active' || signal.status === 'pending'


const PNL_TEXT = { up: 'text-green-400', down: 'text-red-400', flat: 'text-gray-400' } as const

const EXPIRY_TEXT = { ok: 'text-gray-500', soon: 'text-amber-400', expired: 'text-red-400' } as const

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_4.5rem_4.75rem] gap-3 sm:grid-cols-[minmax(0,1fr)_6rem_5.5rem_5.5rem]'

export const DirectionBadge = ({ side }: { side: 'long' | 'short' }) => (
  <span
    className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ${
      side === 'long'
        ? 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30'
        : 'bg-rose-500/10 text-rose-300 ring-rose-500/30'
    }`}
    data-side={side}
  >
    <span aria-hidden="true">{side === 'long' ? '▲' : '▼'}</span>
    {side}
  </span>
)

const PositionRow = ({ signal, sparkline, now }: { signal: TradingSignal; sparkline?: SparklineSeries; now: number }) => {
  const chart = useChartTheme()
  const [base, quote] = displaySymbol(signal.symbol).split('/')
  const price = sparkline?.last_price
  // From the position's side: a short that has fallen 2% reads +2%.
  const pnl = signalPnlPct(signal, price)
  const direction = directionOf(pnl)
  const values = sparkline?.points.map((point) => point.close) ?? []
  const expiry = signalExpiry(signal, now)
  const expiryLabel = !expiry ? 'no expiry' : expiry.state === 'expired' ? 'expired' : `${formatRemaining(expiry.remainingMs)} left`

  return (
    <li
      className={`${ROW_GRID} items-center rounded-md px-2 py-3 transition-colors hover:bg-terminal-raised/60 sm:px-3`}
      data-testid="open-position"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <CoinIcon symbol={signal.symbol} size={18} />
          <span className="truncate font-semibold text-gray-50">
            {base}
            {/* The coin always shows; "/USDT" only where the row has room
                for it beside the badge (the panel is 3/5 of the content). */}
            {quote && <span className="hidden font-normal text-gray-500 2xl:inline">/{quote}</span>}
          </span>
          <DirectionBadge side={signal.signal_type} />
        </div>
        {/* Phones have no timeframe/expiry column, so the subline carries those instead of the entry. */}
        <p className="mt-0.5 truncate font-mono text-[11px] tabular-nums text-gray-500">
          <span className="hidden sm:inline">entry ${formatPrice(signal.entry_price)}</span>
          <span className={`sm:hidden ${expiry ? EXPIRY_TEXT[expiry.state] : ''}`}>
            {signal.timeframe} · {expiryLabel}
          </span>
        </p>
      </div>

      <div className="h-10">
        {values.length > 1 ? (
          <GradientSparkline
            values={values}
            color={chart.colors[direction]}
            baseline={signal.entry_price}
            label={`${displaySymbol(signal.symbol)} over 7 days, entry level dashed`}
          />
        ) : (
          <div className="flex h-full items-center" aria-hidden="true">
            <span className="h-px w-full bg-terminal-border" />
          </div>
        )}
      </div>

      <p
        className={`text-right font-mono text-sm font-semibold tabular-nums ${PNL_TEXT[direction]}`}
        data-testid="position-pnl"
        data-direction={direction}
        title={price != null ? `Last $${formatPrice(price)}` : 'Latest price unavailable'}
      >
        {pnl == null ? '—' : formatPnlPct(pnl)}
      </p>

      <div className="hidden text-right font-mono text-xs tabular-nums sm:block">
        <p className="text-gray-300">{signal.timeframe}</p>
        <p
          className={expiry ? EXPIRY_TEXT[expiry.state] : 'text-gray-600'}
          title={expiry ? `Expires ${format(expiry.expiresAt, 'MMM d, HH:mm')}` : undefined}
        >
          {expiryLabel}
        </p>
      </div>
    </li>
  )
}

/**
 * The engine's live open signals: side, a 7-day price line with the entry
 * level dashed across it, P&L since entry at the latest price, timeframe and
 * time to expiry. Newest first, capped, with the rest on the Signals page.
 */
const OpenPositionsPanel = () => {
  const [signals, setSignals] = useState<TradingSignal[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [sparklines, setSparklines] = useState<Record<string, SparklineSeries>>({})
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let active = true
    let loaded = false

    const load = async () => {
      // A background tab needn't poll; the next visible poll catches up.
      if (loaded && document.hidden) return
      try {
        const rows = await signalsAPI.list({ demo: false, limit: LIST_LIMIT })
        if (!active) return
        // The engine's positions only: the list also returns the caller's own
        // locked calls (trading_mode 'creator'), which are theirs, not the
        // engine's, and which the Open signals tile never counted.
        const open = rows
          .filter((signal) => isOpenSignal(signal) && signal.trading_mode !== 'creator')
          .sort((a, b) => parseApiDate(b.created_at).getTime() - parseApiDate(a.created_at).getTime())
        loaded = true
        setSignals(open)
        setFailed(false)
        setNow(Date.now())

        const symbols = Array.from(new Set(open.slice(0, MAX_POSITION_ROWS).map((signal) => signal.symbol)))
        if (symbols.length === 0) return
        try {
          const data = await marketAPI.getSparklines(symbols)
          if (active) setSparklines(Object.fromEntries(data.sparklines.map((series) => [series.symbol, series])))
        } catch {
          // Display only: rows render without a price line or P&L.
        }
      } catch (error) {
        if (!active) return
        // Keep the last list on screen; the next poll retries.
        console.error('Open positions refresh failed:', error)
        setFailed(true)
      }
    }

    load()
    const timer = setInterval(load, POSITIONS_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  const total = signals?.length ?? 0
  const longs = signals?.filter((signal) => signal.signal_type === 'long').length ?? 0
  // Live, so listed, but outside the record the Open signals tile counts.
  const excluded = signals?.filter((signal) => signal.excluded_from_record).length ?? 0
  const shown = signals?.slice(0, MAX_POSITION_ROWS) ?? []

  return (
    <TerminalPanel
      title="Open positions"
      tone="sky"
      className="h-full"
      subtitle={
        signals && (
          <p className="font-mono text-xs tabular-nums text-gray-500" data-testid="open-positions-counts">
            {total} open · {longs} long · {total - longs} short
            {excluded > 0 && ` · ${excluded} outside the record`}
          </p>
        )
      }
      action={
        <Link to="/signals" className="text-xs font-semibold text-primary-400 hover:text-primary-300">
          {total > MAX_POSITION_ROWS ? `View all ${total}` : 'View all'} →
        </Link>
      }
    >
      {signals == null ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {failed ? 'Open positions are unavailable right now.' : 'Loading positions…'}
        </p>
      ) : shown.length === 0 ? (
        <div className="rounded-md border border-dashed border-terminal-border py-10 text-center">
          <p className="text-sm text-gray-300">No open positions</p>
          <p className="mt-1 text-xs text-gray-500">The engine opens one when a setup clears every gate.</p>
        </div>
      ) : (
        <>
          <div
            className={`${ROW_GRID} hidden border-b border-terminal-border/70 px-3 pb-2 text-[10px] font-medium uppercase tracking-wider text-gray-500 sm:grid`}
            aria-hidden="true"
          >
            <span>Position</span>
            <span>7d price</span>
            <span className="text-right">P&amp;L</span>
            <span className="text-right">TF · Expiry</span>
          </div>
          <ul className="divide-y divide-terminal-border/40">
            {shown.map((signal) => (
              <PositionRow key={signal.id} signal={signal} sparkline={sparklines[signal.symbol]} now={now} />
            ))}
          </ul>
          {total > MAX_POSITION_ROWS && (
            <p className="mt-3 text-center text-xs text-gray-500">
              +{total - MAX_POSITION_ROWS} more on the{' '}
              <Link to="/signals" className="text-primary-400 hover:text-primary-300">
                Signals page
              </Link>
            </p>
          )}
        </>
      )}
    </TerminalPanel>
  )
}

export default OpenPositionsPanel
